/* utils/services/PlaceSearchService.ts ----------------------------------- *
 * HiiKER's own place search: GET /api/places/search and /api/places/reverse  *
 * (Api::PlacesController). The server calls our Photon geocoder (Stadia as  *
 * a fallback), normalises the answer and ranks hiking places sensibly, so    *
 * every result already carries its coordinates — no second lookup on pick.   *
 * ------------------------------------------------------------------------- */

import { apiProxyHeaders } from '../apiProxy';

export const PLACES_PATH = '/api/places';

/**
 * peak, volcano, mountain_range, national_park, protected_area, nature_reserve, glacier, water,
 * city, town, village, hamlet, suburb, locality, county, state, country, street, address, poi.
 * The server may add kinds later, so treat anything unknown as a generic place.
 */
export type PlaceKind = string;

export interface PlaceAddress {
  housenumber: string | null;
  street: string | null;
  postcode: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  country: string | null;
}

export interface PlaceResult {
  id: string;
  name: string;
  /** name + ", " + context, or just the name. */
  label: string;
  /** A short locality such as "County Kerry, Ireland", or null. */
  context: string | null;
  kind: PlaceKind;
  lat: number;
  lon: number;
  /** [west, south, east, north], or null for a point. */
  bbox: [number, number, number, number] | null;
  country: string | null;
  /** Lowercase ISO 3166-1 alpha-2. */
  country_code: string | null;
  address: PlaceAddress;
}

export interface PlaceSearchResponse {
  provider: 'photon' | 'stadia';
  results: PlaceResult[];
}

export interface PlaceSearchOptions {
  /** [lon, lat] — ranks places near it first without excluding anything further away. */
  focus?: number[] | null;
  /** 1–10, server default 5. */
  limit?: number;
  /** Result language; defaults to the page's language. */
  lang?: string | null;
  signal?: AbortSignal;
}

export interface PlaceReverseOptions {
  /** 1–5, server default 1. */
  limit?: number;
  lang?: string | null;
  signal?: AbortSignal;
}

/** The page's language ("de" for <html lang="de-AT">), or null when there is none. */
export function pageLang(doc: Document | null = globalThis.document ?? null): string | null {
  const lang = doc?.documentElement?.lang?.trim();
  return lang ? lang.split('-')[0].toLowerCase() : null;
}

function isCoordinatePair(focus: unknown): focus is number[] {
  return (
    Array.isArray(focus) &&
    focus.length >= 2 &&
    Number.isFinite(focus[0]) &&
    Number.isFinite(focus[1]) &&
    Math.abs(focus[0]) <= 180 &&
    Math.abs(focus[1]) <= 90
  );
}

export function placeSearchUrl(query: string, options: Omit<PlaceSearchOptions, 'signal'> = {}): string {
  const params = new URLSearchParams({ q: query.trim() });
  if (isCoordinatePair(options.focus)) {
    params.set('lat', String(options.focus[1]));
    params.set('lon', String(options.focus[0]));
  }
  if (options.limit) params.set('limit', String(options.limit));
  const lang = options.lang === undefined ? pageLang() : options.lang;
  if (lang) params.set('lang', lang);
  return `${PLACES_PATH}/search?${params.toString()}`;
}

export function placeReverseUrl(lon: number, lat: number, options: Omit<PlaceReverseOptions, 'signal'> = {}): string {
  const params = new URLSearchParams({ lat: String(lat), lon: String(lon) });
  if (options.limit) params.set('limit', String(options.limit));
  const lang = options.lang === undefined ? pageLang() : options.lang;
  if (lang) params.set('lang', lang);
  return `${PLACES_PATH}/reverse?${params.toString()}`;
}

async function getPlaces(url: string, signal?: AbortSignal): Promise<PlaceSearchResponse> {
  // Same-origin: a signed-in visitor's session cookie rides along, and the CSRF token is how a
  // logged-out visitor is let through (the same caller model as /api/proxy).
  const response = await fetch(url, { headers: { Accept: 'application/json', ...apiProxyHeaders() }, signal });
  if (!response.ok) throw new Error(`Place search failed: ${response.status}`);
  const data = (await response.json()) as PlaceSearchResponse;
  return { provider: data?.provider ?? 'photon', results: Array.isArray(data?.results) ? data.results : [] };
}

/** Places matching `query`, best first. Throws when the search fails; a blank query is []. */
export async function searchPlaces(query: string, options: PlaceSearchOptions = {}): Promise<PlaceResult[]> {
  if (!query.trim()) return [];
  const { signal, ...rest } = options;
  return (await getPlaces(placeSearchUrl(query, rest), signal)).results;
}

/** The place at a point, or null when there is nothing there or the lookup fails. */
export async function reversePlace(lon: number, lat: number, options: PlaceReverseOptions = {}): Promise<PlaceResult | null> {
  try {
    const { signal, ...rest } = options;
    return (await getPlaces(placeReverseUrl(lon, lat, rest), signal)).results[0] ?? null;
  } catch (error) {
    console.warn('Reverse place lookup failed:', error);
    return null;
  }
}

/** "Main Street, Killarney" — a place short enough for a route card. */
export function shortPlaceLabel(place: Pick<PlaceResult, 'name' | 'label' | 'address'> | null | undefined): string | null {
  if (!place) return null;
  const first = place.name || place.address?.street || null;
  const area = place.address?.city || place.address?.county || place.address?.state || null;
  if (first && area && !first.includes(area)) return `${first}, ${area}`;
  return first || area || place.label || null;
}

/** mapbox-gl LngLatBounds-like [[west, south], [east, north]] for a place with an extent, else null. */
export function placeBounds(place: Pick<PlaceResult, 'bbox'>): [[number, number], [number, number]] | null {
  const bbox = place.bbox;
  if (!bbox || bbox.length !== 4 || !bbox.every(Number.isFinite)) return null;
  const [west, south, east, north] = bbox;
  if (west === east && south === north) return null;
  return [
    [west, south],
    [east, north]
  ];
}

/** Reverse lookup straight to a short label (route cards, planner start points). */
export async function reversePlaceLabel(lon: number, lat: number, options: PlaceReverseOptions = {}): Promise<string | null> {
  return shortPlaceLabel(await reversePlace(lon, lat, options));
}
